import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as ContentCopyIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { contentService, Content, ContentGenerationRequest } from '../services/content';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

export default function ContentPage() {
  const [content, setContent] = useState<Content[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openGenerate, setOpenGenerate] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState<string>('');
  const [request, setRequest] = useState<ContentGenerationRequest>({
    content_type: 'blog_post',
    topic: '',
    tone: 'professional',
    platform: 'linkedin',
    target_audience: '',
    keywords: [],
    length: 'medium',
  });

  const workspaceId = 'default-workspace';

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      setLoading(true);
      const data = await contentService.getContent();
      setContent(data.content || []);
    } catch (err: any) {
      console.error('Failed to load content:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      const result = await contentService.generateContent(request);
      
      // Check if response contains workflow_id (orchestrator-powered endpoint)
      if (result.workflow_id) {
        const workflowId = result.workflow_id;
        setSuccess(`Content workflow created! Workflow ID: ${workflowId}. Executing multi-agent workflow...`);
        
        // Automatically execute the workflow (orchestrator delegates to agents)
        await axios.post(
          `${API_URL}/workflows/${workflowId}/execute`,
          {},
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
          }
        );
        
        setOpenGenerate(false);
        setSuccess(`Content generation executing! Agents working: Content Architect → Content Generation → Compliance. View progress in Workflows page.`);
        
        // Reload content after workflow has time to complete
        setTimeout(() => loadContent(), 5000);
      } else {
        // Legacy direct generation response
        setGeneratedContent(result.content || '');
      }
      setError(null);
    } catch (err: any) {
      setError('Failed to generate content');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    try {
      await contentService.createContent({
        title: request.topic,
        content_type: request.content_type,
        content: generatedContent,
        platform: request.platform,
        status: 'draft',
      });
      setOpenGenerate(false);
      setGeneratedContent('');
      setRequest({ content_type: 'blog_post', topic: '', tone: 'professional', platform: 'linkedin' });
      loadContent();
    } catch (err: any) {
      setError('Failed to save content');
    }
  };

  const getStatusColor = (status: string): any => {
    const colors: any = {
      draft: 'default',
      scheduled: 'info',
      published: 'success',
    };
    return colors[status] || 'default';
  };

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 0 } }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' },
        mb: { xs: 2, sm: 3 },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 },
        width: '100%'
      }}>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>Content</Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />} 
          onClick={() => setOpenGenerate(true)}
          fullWidth={window.innerWidth < 600}
        >
          Generate Content
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : content.length > 0 ? (
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {content.map((item) => (
            <Grid item xs={12} sm={6} md={6} lg={4} key={item.id}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom noWrap>
                    {item.title}
                  </Typography>
                  <Box display="flex" gap={1} mb={2}>
                    <Chip label={item.content_type} size="small" />
                    <Chip label={item.status} color={getStatusColor(item.status)} size="small" />
                  </Box>
                  {item.platform && (
                    <Typography variant="body2" color="textSecondary">
                      Platform: {item.platform}
                    </Typography>
                  )}
                </CardContent>
                <CardActions>
                  <Button size="small" startIcon={<ContentCopyIcon />}>
                    Copy
                  </Button>
                  <Button size="small" startIcon={<ShareIcon />}>
                    Publish
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper sx={{ p: 3 }}>
          <Typography color="textSecondary">
            No content yet. Use AI to generate emails, posts, and more.
          </Typography>
        </Paper>
      )}

      <Dialog open={openGenerate} onClose={() => setOpenGenerate(false)} maxWidth="md" fullWidth>
        <DialogTitle>Generate Content</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Content Type"
                value={request.content_type}
                onChange={(e) => setRequest({ ...request, content_type: e.target.value })}
              >
                <MenuItem value="blog_post">Blog Post</MenuItem>
                <MenuItem value="social_post">Social Post</MenuItem>
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="landing_page">Landing Page</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Platform"
                value={request.platform}
                onChange={(e) => setRequest({ ...request, platform: e.target.value })}
              >
                <MenuItem value="linkedin">LinkedIn</MenuItem>
                <MenuItem value="twitter">Twitter</MenuItem>
                <MenuItem value="facebook">Facebook</MenuItem>
                <MenuItem value="instagram">Instagram</MenuItem>
                <MenuItem value="blog">Blog/Website</MenuItem>
                <MenuItem value="email">Email</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Topic"
                value={request.topic}
                onChange={(e) => setRequest({ ...request, topic: e.target.value })}
                placeholder="e.g., AI in Marketing Automation"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Tone"
                value={request.tone}
                onChange={(e) => setRequest({ ...request, tone: e.target.value })}
              >
                <MenuItem value="professional">Professional</MenuItem>
                <MenuItem value="casual">Casual</MenuItem>
                <MenuItem value="enthusiastic">Enthusiastic</MenuItem>
                <MenuItem value="educational">Educational</MenuItem>
                <MenuItem value="persuasive">Persuasive</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Length"
                value={request.length || 'medium'}
                onChange={(e) => setRequest({ ...request, length: e.target.value })}
              >
                <MenuItem value="short">Short (100-300 words)</MenuItem>
                <MenuItem value="medium">Medium (300-600 words)</MenuItem>
                <MenuItem value="long">Long (600-1000 words)</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Target Audience (Optional)"
                value={request.target_audience || ''}
                onChange={(e) => setRequest({ ...request, target_audience: e.target.value })}
                placeholder="e.g., B2B SaaS founders, Marketing managers"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Keywords (Optional)"
                value={request.keywords?.join(', ') || ''}
                onChange={(e) => setRequest({ ...request, keywords: e.target.value.split(',').map(k => k.trim()).filter(Boolean) })}
                placeholder="Comma-separated: marketing automation, AI, productivity"
              />
            </Grid>
            {generatedContent && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={10}
                  label="Generated Content"
                  value={generatedContent}
                  onChange={(e) => setGeneratedContent(e.target.value)}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenGenerate(false)}>Cancel</Button>
          {!generatedContent ? (
            <Button
              onClick={handleGenerate}
              variant="contained"
              disabled={!request.topic || generating}
            >
              {generating ? <CircularProgress size={24} /> : 'Generate with AI'}
            </Button>
          ) : (
            <>
              <Button onClick={handleGenerate} disabled={generating}>
                Regenerate
              </Button>
              <Button onClick={handleSave} variant="contained">
                Save Content
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
